import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSignUp } from '@/hooks/use-auth'
import { AimeLogo } from '@/components/AimeLogo'

export default function SignUpPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const signUp = useSignUp()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    signUp.mutate(
      { email, password, name },
      {
        onSuccess: () => navigate('/', { replace: true }),
      },
    )
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <AimeLogo size={120} />
          <h1 className="sr-only">Aime — {t('auth.signUp')}</h1>
          <p className="text-on-surface-variant text-sm">The CRM for everyday relationships.</p>
          <p className="text-on-surface-variant mt-1">{t('auth.signUp')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="name">
              {t('auth.firstName')}
            </label>
            <input
              id="name"
              type="text"
              required
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-outline-variant/30 bg-surface-container ring-offset-background focus-visible:ring-ring mt-1 flex h-10 w-full rounded-xl border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="email">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-outline-variant/30 bg-surface-container ring-offset-background focus-visible:ring-ring mt-1 flex h-10 w-full rounded-xl border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="password">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border-outline-variant/30 bg-surface-container ring-offset-background focus-visible:ring-ring mt-1 flex h-10 w-full rounded-xl border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="confirmPassword">
              {t('auth.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="border-outline-variant/30 bg-surface-container ring-offset-background focus-visible:ring-ring mt-1 flex h-10 w-full rounded-xl border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
          </div>

          {(error || signUp.error) && (
            <p className="text-destructive text-sm">{error || signUp.error?.message}</p>
          )}

          <button
            type="submit"
            disabled={signUp.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 w-full items-center justify-center rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {signUp.isPending ? '...' : t('auth.signUp')}
          </button>
        </form>

        <p className="text-on-surface-variant text-center text-sm">
          {t('auth.hasAccount')}{' '}
          <Link to="/sign-in" className="text-primary hover:underline">
            {t('auth.signIn')}
          </Link>
        </p>
      </div>
    </div>
  )
}
